import { Injectable, signal, computed, inject, effect, untracked } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject, filter, iif, map, of, tap, withLatestFrom } from 'rxjs';

import { equals, isNotBlank, isPresent, isDeepEqual } from '@fcom/core';
import { FlightSearchParams, GlobalBookingTripDates, RecommendationService, PriceCalendarService } from '@fcom/common';
import {
  CommonFeatureState,
  GlobalBookingActions,
  selectGlobalBookingDiscountCode,
  GlobalBookingState,
  selectGlobalBookingFlights,
  selectGlobalBookingPaxAmount,
  selectGlobalBookingState,
  selectGlobalBookingTravelClass,
  selectGlobalBookingTravelDates,
  selectGlobalBookingTripType,
} from '@fcom/common/store';
import { GlobalBookingTravelClass, TripType } from '@fcom/core/interfaces';
import { PaxAmount } from '@fcom/dapi/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { loginStatusSelector, profileSelector } from '@fcom/core/selectors';

import { BookingWidgetGtmService } from './booking-widget-gtm.service';
import { createDeeplinkPathFromFlightSearchParams, hasCorrectAmountOfFlights } from '../utils/utils';
import {
  BookingWidgetActions,
  BookingWidgetAppState,
  selectActiveTab,
  selectAirCalendarPrices,
  selectNotificationWarnings,
} from '../store';
import {
  InstantSearchBaseParams,
  PreviousSearch,
  WidgetTab,
  PriceParams,
  NotificationWarning,
  SelectorType,
  GlobalBookingWidgetSelectionChangeMap,
} from '../interfaces';
import { BookingWidgetCalendarService } from './booking-widget-calendar.service';
import { BookingWidgetFlightService } from './booking-widget-flight.service';
import { BookingWidgetTripService } from './booking-widget-trip.service';

@Injectable({ providedIn: 'root' })
export class BookingWidgetService {
  private store$ = inject<Store<CommonFeatureState & BookingWidgetAppState>>(Store);
  private languageService = inject(LanguageService);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);
  private recommendationService = inject(RecommendationService);
  private bookingWidgetCalendarService = inject(BookingWidgetCalendarService);
  private router = inject(Router);
  private bookingWidgetFlightService = inject(BookingWidgetFlightService);
  private bookingWidgetTripService = inject(BookingWidgetTripService);
  private priceCalendarService = inject(PriceCalendarService);

  private readonly _globalBookingState = this.store$.selectSignal(selectGlobalBookingState);
  private readonly _discountCodeSignal = this.store$.selectSignal(selectGlobalBookingDiscountCode);
  private readonly _travelDatesSignal = this.store$.selectSignal(selectGlobalBookingTravelDates);
  private readonly _tripTypeSignal = this.store$.selectSignal(selectGlobalBookingTripType);
  private readonly _travelClassSignal = this.store$.selectSignal(selectGlobalBookingTravelClass);
  private readonly _flightsSignal = this.store$.selectSignal(selectGlobalBookingFlights);
  private readonly _paxAmountSignal = this.store$.selectSignal(selectGlobalBookingPaxAmount);
  private readonly _activeTabSignal = this.store$.selectSignal(selectActiveTab);

  private _flexibleDates = signal(false);
  private _showCompact = signal(true);
  private _originalBookingSelection = signal<GlobalBookingState | undefined>(undefined);
  private _globalBookingWidgetSelectionChanges = signal<GlobalBookingWidgetSelectionChangeMap>({});
  private usePopoverSelectorsSignal = signal(false);
  private _currentSearch = signal<FlightSearchParams | null>(null);

  private _modifyType = signal<SelectorType | null>(null);

  readonly usePopoverSelectors = this.usePopoverSelectorsSignal.asReadonly();
  readonly currentSearch = this._currentSearch.asReadonly();
  readonly modifyType = this._modifyType.asReadonly();
  readonly originalBookingSelection = this._originalBookingSelection.asReadonly();
  readonly globalBookingWidgetSelectionChanges = this._globalBookingWidgetSelectionChanges.asReadonly();
  readonly showCompact = this._showCompact.asReadonly();
  readonly flexibleDates = this._flexibleDates.asReadonly();
  readonly loginStatus = this.store$.selectSignal(loginStatusSelector);
  readonly profile = this.store$.selectSignal(profileSelector);
  readonly notificationWarning = this.store$.selectSignal(selectNotificationWarnings);
  readonly airCalendarPrices = this.store$.selectSignal(selectAirCalendarPrices);
  readonly discountCode = computed(() => {
    const value = this._discountCodeSignal();
    return isPresent(value) ? value : null;
  });
  readonly travelDates = computed(() => this._travelDatesSignal(), {
    equal: (prev, next) =>
      prev.departureDate?.id === next.departureDate?.id && prev.returnDate?.id === next.returnDate?.id,
  });
  readonly bookingSelections = computed(() => this._globalBookingState(), {
    equal: (a, b) => isDeepEqual(a, b),
  });

  // Read synchronously from store-backed signals (not toObservable): startNewSearch() /
  // navigateToBookingFlow() snapshot this in the same tick as the dispatch that changed the
  // selection (e.g. location selection starts the search immediately), and a toObservable
  // emission would be deferred to the next effect flush, replaying stale params.
  readonly flightSearchParams = computed<FlightSearchParams>(() => ({
    tripType: this._tripTypeSignal() ?? TripType.RETURN,
    travelClass: this._travelClassSignal(),
    flights: this._flightsSignal(),
    paxAmount: this._paxAmountSignal(),
    locale: this.languageService.localeValue,
    isAward: this._activeTabSignal() === WidgetTab.AWARD,
    promoCode: this.discountCode(),
    isCorporate: this.profile()?.isCorporate,
  }));

  readonly continueEnabled = computed(
    () => this.bookingWidgetTripService.routeCffReady() && this.validateFlightSearchParams(this.flightSearchParams())
  );

  readonly isMultiCity = computed(() => this.flightSearchParams().flights.length > 2);

  private readonly _priceRequestBaseParams = computed<InstantSearchBaseParams | undefined>(
    () => {
      const tab = this.bookingWidgetTripService.activeTab();
      if (tab !== WidgetTab.FLIGHT && tab !== WidgetTab.AWARD) {
        return undefined;
      }

      const locations = this.bookingWidgetFlightService.locations();
      if (!(locations.origin && locations.destination)) {
        return undefined;
      }

      const params = {
        tripType: this.bookingWidgetTripService.selectedTripType(),
        travelClass: this.bookingWidgetTripService.selectedTravelClass(),
        locations,
        paxAmount: this.bookingWidgetTripService.paxAmount(),
      };

      if (!this.priceParamsAreValid(params) || params.tripType === TripType.MULTICITY) {
        return undefined;
      }

      return this.mapToPriceRequestBaseParams(params);
    },
    {
      equal: (prev, next) => {
        if (prev === undefined || next === undefined) {
          return prev === next;
        }
        return !this.priceBaseParamsChanged(prev, next);
      },
    }
  );

  constructor() {
    effect(() => {
      if (this.bookingWidgetTripService.selectedTripType() === TripType.MULTICITY) {
        this.store$.dispatch(
          BookingWidgetActions.clearNotificationWarning({ key: NotificationWarning.SEASONAL_ROUTE })
        );
      }
    });

    // The warnings are read untracked so clearing them doesn't re-trigger the effect.
    effect(() => {
      this.bookingWidgetTripService.selectedTripType();
      this.bookingWidgetFlightService.flights();
      this.bookingWidgetTripService.activeTab();
      const warnings = untracked(() => this.notificationWarning());
      if (Object.keys(warnings).length > 0) {
        this.store$.dispatch(BookingWidgetActions.clearNoFlightsNotificationWarnings());
      }
    });

    effect(() => {
      const baseParams = this._priceRequestBaseParams();
      if (baseParams === undefined) {
        return;
      }
      const warnings = untracked(() => this.notificationWarning());
      if (Object.keys(warnings).length > 0) {
        this.store$.dispatch(
          BookingWidgetActions.clearNotificationWarning({ key: NotificationWarning.SEASONAL_ROUTE })
        );
      }
    });

    let previousPreFlightSearchFlights: FlightSearchParams['flights'] | undefined;
    effect(() => {
      const flightSearchParams = this.flightSearchParams();
      if (!this.validateFlightSearchParams(flightSearchParams)) {
        return;
      }
      if (
        !isPresent(flightSearchParams.flights[0]?.origin?.type) ||
        !isPresent(flightSearchParams.flights[0]?.destination?.type)
      ) {
        return;
      }
      if (
        previousPreFlightSearchFlights !== undefined &&
        equals(previousPreFlightSearchFlights, flightSearchParams.flights)
      ) {
        return;
      }
      previousPreFlightSearchFlights = flightSearchParams.flights;
      this.bookingWidgetGtmService.preFlightSearch(flightSearchParams);
    });

    effect(() => {
      const { origin, destination } = this.bookingWidgetFlightService.locations();
      const hasBothLocations = isNotBlank(origin?.locationCode) && isNotBlank(destination?.locationCode);
      if (!hasBothLocations) {
        BookingWidgetActions.resetPrices();
      }
      this._showCompact.set(!hasBothLocations);
    });

    this.setCurrentSearchToCurrentParams();
  }

  setModifyType(type: SelectorType): void {
    this._modifyType.set(type);
  }

  resetModifyType(): void {
    this._modifyType.set(null);
  }

  setCurrentSearchToCurrentParams(): void {
    this._currentSearch.set({
      tripType: this.bookingWidgetTripService.selectedTripType(),
      travelClass: this.bookingWidgetTripService.selectedTravelClass(),
      flights: this.bookingWidgetFlightService.flights(),
      paxAmount: this.bookingWidgetTripService.paxAmount(),
      locale: this.languageService.localeValue,
      isAward: this.bookingWidgetTripService.activeTab() === WidgetTab.AWARD,
      promoCode: this.discountCode(),
      isCorporate: this.profile()?.isCorporate,
    });
  }

  selectPreviousSearch(previousSearch: PreviousSearch): void {
    // Capture the original selection before the previous search is applied to the store,
    // then diff against it to flag the selectors the previous search will change.
    this.captureOriginalSelectionIfUnset();
    this.previousSearchChanged(previousSearch, this._originalBookingSelection()).forEach((changedType) => {
      this.setSelectionChangeType(changedType);
    });
    this.bookingWidgetFlightService.setPreviousFlightSelection(previousSearch);
  }

  setTravelDates(
    dates: GlobalBookingTripDates,
    index: number,
    isAirCalendar = false,
    isGlobalBookingWidget = false
  ): void {
    if (isGlobalBookingWidget) {
      this.setSelectionChangeType(SelectorType.TRAVEL_DATES);
    }

    if (!isAirCalendar) {
      // TODO: check if we would like a separate and different multi-city event as this does not really translate for that purpose
      this.bookingWidgetGtmService.trackElementEvent(
        'travel-dates',
        `DEPARTURE: ${dates.departureDate}, RETURN: ${dates.returnDate}`
      );
    }

    this.store$.dispatch(GlobalBookingActions.setFlightDate({ dates, index }));
  }

  setPaxAmount(paxAmount: PaxAmount): void {
    this.store$.dispatch(GlobalBookingActions.setPaxAmount({ paxAmount }));
  }

  setTripType(tripType: TripType): void {
    this.store$.dispatch(GlobalBookingActions.setTripType({ tripType }));
  }

  setDiscountCode(discountCode: string, isGlobalBookingWidget = false): void {
    if (isGlobalBookingWidget) {
      this.setSelectionChangeType(SelectorType.DISCOUNT_CODE);
    }
    this.store$.dispatch(GlobalBookingActions.setDiscountCode({ discountCode }));
    if (discountCode) {
      this.bookingWidgetGtmService.trackElementEvent('promo-code-modal-done', discountCode);
    }
  }

  setFlexibleDates(isFlexibleDates: boolean): void {
    this._flexibleDates.set(isFlexibleDates);
  }

  validateFlightSearchParams({ flights, tripType, travelClass, paxAmount }: FlightSearchParams): boolean {
    const travelClassIsValid = isPresent(travelClass);
    const paxAmountIsValid = paxAmount.adults >= 1;
    const flightsAreValid = flights.every((flight, i) => {
      const previousFlight = flights[i - 1];
      const hasConsecutiveDates = !previousFlight || flight.departureDate?.gte(previousFlight.departureDate);
      return (
        flight.origin &&
        flight.origin?.locationCode &&
        flight.destination &&
        flight.destination?.locationCode &&
        flight.departureDate &&
        hasConsecutiveDates
      );
    });

    return travelClassIsValid && paxAmountIsValid && flightsAreValid && hasCorrectAmountOfFlights(tripType, flights);
  }

  resetSelection(): void {
    this.store$.dispatch(GlobalBookingActions.resetSelection());
  }

  expandCompactWidget(): void {
    if (this._showCompact()) {
      this._showCompact.set(false);
    }
  }

  setCompactWidget(showCompactWigetStatus: boolean): void {
    this._showCompact.set(showCompactWigetStatus);
  }

  startNewSearch(enableNewSearchAutomatically = false): void {
    if (!enableNewSearchAutomatically) {
      this.bookingWidgetGtmService.trackElementEvent('global-widget-start-new-search');
    }
    this.resetLocalOriginalBookingSelection();

    // Snapshot synchronously: callers dispatch the selection change in the same tick (e.g. location
    // selection starts the search immediately), so the computed must be read now, not deferred.
    const flightSearchParams = this.flightSearchParams();
    if (!this.validateFlightSearchParams(flightSearchParams)) {
      return;
    }

    this._currentSearch.set(flightSearchParams);
    this.bookingWidgetFlightService.setPreviousSearchToLocalStorage(flightSearchParams);
    this.router.navigateByUrl(
      createDeeplinkPathFromFlightSearchParams({
        ...flightSearchParams,
        locale: this.languageService.langValue,
      })
    );
  }

  resetSearchSelection(): void {
    this._globalBookingWidgetSelectionChanges.set({});
  }

  navigateToBookingFlow(
    fromMatrix = false,
    airCalendarPrice: string | undefined = undefined,
    loading$: Subject<boolean>
  ): void {
    if (!this.continueEnabled()) {
      return;
    }

    const flightSearchParams = this.flightSearchParams();
    loading$.next(true);
    this.sendRecommendationsFlightSearchEvent(flightSearchParams);

    const continueToBooking$ = fromMatrix
      ? of(true)
      : iif(
          () => this.flexibleDates() || flightSearchParams.isAward,
          this.bookingWidgetCalendarService.getAirCalendar(flightSearchParams).pipe(map(() => false)),
          this.bookingWidgetCalendarService.checkFlightAvailabilityAndContinue(flightSearchParams)
        );

    continueToBooking$
      .pipe(
        withLatestFrom(this.priceCalendarService.startingFromPrice$),
        tap(() => loading$.next(false)),
        filter(([continueToBooking]) => continueToBooking),
        filter(() => this.validateFlightSearchParams(flightSearchParams))
      )
      .subscribe(([, startingPrice]) => {
        loading$.next(true);
        this.bookingWidgetFlightService.setPreviousSearchToLocalStorage(flightSearchParams);
        this.store$.dispatch(
          BookingWidgetActions.setInstantSearchMonitoring({
            instantSearchMonitoring: {
              airCalendarPrice,
              instantSearchPrice: startingPrice?.amount,
            },
          })
        );
        this.router
          .navigateByUrl(
            createDeeplinkPathFromFlightSearchParams({
              ...flightSearchParams,
              locale: this.languageService.langValue,
            })
          )
          .finally(() => loading$.next(false));
      });
  }

  navigateToMultiCityBookingFlow(loading$: Subject<boolean>): void {
    if (!this.continueEnabled()) {
      return;
    }

    const flightSearchParams = this.flightSearchParams();
    loading$.next(true);
    loading$.next(false);

    if (!this.validateFlightSearchParams(flightSearchParams)) {
      return;
    }

    this.bookingWidgetFlightService.setPreviousSearchToLocalStorage(flightSearchParams);
    this.router.navigateByUrl(
      createDeeplinkPathFromFlightSearchParams({
        ...flightSearchParams,
        locale: this.languageService.langValue,
      })
    );
  }

  setUsePopoverSelectors(value: boolean): void {
    this.usePopoverSelectorsSignal.set(value);
  }

  resetLocalOriginalBookingSelection(): void {
    this._globalBookingWidgetSelectionChanges.set({});
    this._originalBookingSelection.set(undefined);
  }

  setGlobalBookingSelectionToOriginal(): void {
    this.store$.dispatch(
      GlobalBookingActions.updateSelection({
        selection: this._originalBookingSelection(),
      })
    );
    this.resetLocalOriginalBookingSelection();
  }

  setSelectionChangeType(type: SelectorType): void {
    this.captureOriginalSelectionIfUnset();
    const currentChange = this._globalBookingWidgetSelectionChanges();
    if (!currentChange[type]) {
      this._globalBookingWidgetSelectionChanges.set({
        ...currentChange,
        [type]: true,
      });
    }
  }

  /**
   * Snapshots the global booking state as the original selection on the first widget change,
   * so the summary header can diff against it and setGlobalBookingSelectionToOriginal can revert
   * to it. Must run synchronously before the change is dispatched to the store.
   */
  private captureOriginalSelectionIfUnset(): void {
    if (!this._originalBookingSelection()) {
      this._originalBookingSelection.set(this._globalBookingState());
    }
  }

  private previousSearchChanged(
    previousSearches: PreviousSearch,
    originalBookingSelection: GlobalBookingState
  ): SelectorType[] {
    const selectionChangeType = [];
    if (!equals(previousSearches.paxAmount, originalBookingSelection.paxAmount)) {
      selectionChangeType.push(SelectorType.PAX);
    }
    if (!equals(previousSearches.tripType, originalBookingSelection.tripType)) {
      selectionChangeType.push(SelectorType.TRIP_TYPE);
    }
    if (previousSearches.flights[0].origin.locationCode !== originalBookingSelection.flights[0].origin.locationCode) {
      selectionChangeType.push(SelectorType.ORIGIN);
    }
    if (
      previousSearches.flights[0].destination.locationCode !==
      originalBookingSelection.flights[0].destination.locationCode
    ) {
      selectionChangeType.push(SelectorType.DESTINATION);
    }
    if (
      !equals(
        previousSearches.flights.map(({ departureDate }) => departureDate),
        originalBookingSelection.flights.map(({ departureDate }) => departureDate)
      )
    ) {
      selectionChangeType.push(SelectorType.TRAVEL_DATES);
    }
    return selectionChangeType;
  }

  private sendRecommendationsFlightSearchEvent(params: FlightSearchParams): void {
    const { origin, destination } = params.flights[0];

    this.recommendationService
      .sendFlightSearchEvent(
        origin,
        destination,
        params.flights[0].departureDate.toISOString().substring(0, 10),
        params.flights[1]?.departureDate?.toISOString().substring(0, 10),
        // combining adults + c15s was in the initial requirement and logic was the same in the old widget
        params.paxAmount.adults + params.paxAmount.c15s,
        params.paxAmount.children + params.paxAmount.infants
      )
      .subscribe();
  }

  private priceParamsAreValid({ locations, travelClass, tripType, paxAmount }: PriceParams): boolean {
    return (
      isPresent(travelClass) &&
      isPresent(tripType) &&
      isPresent(locations?.origin?.locationCode) &&
      isPresent(locations?.destination?.locationCode) &&
      paxAmount?.adults >= 1
    );
  }

  private priceBaseParamsChanged(prev: InstantSearchBaseParams, next: InstantSearchBaseParams): boolean {
    return (
      prev.departureLocationCode !== next.departureLocationCode ||
      prev.destinationLocationCode !== next.destinationLocationCode ||
      prev.travelClass !== next.travelClass ||
      prev.oneway !== next.oneway
    );
  }

  private mapToPriceRequestBaseParams({
    tripType,
    travelClass,
    locations,
    paxAmount,
  }: PriceParams): InstantSearchBaseParams {
    return {
      departureLocationCode: locations.origin.locationCode,
      destinationLocationCode: locations.destination.locationCode,
      travelClass: travelClass === GlobalBookingTravelClass.MIXED ? undefined : travelClass,
      adults: paxAmount.adults + paxAmount.c15s,
      children: paxAmount.children,
      infants: paxAmount.infants,
      numberOfDays: 360,
      oneway: tripType === TripType.ONEWAY,
      directFlights: false,
      locale: this.languageService.localeValue,
    };
  }
}
